import React from 'react';
import { McDialog, McButton, McIcon } from '@maersk-global/mds-react-wrapper';

interface SqlDiffDialogProps {
  open: boolean;
  onClose: () => void;
  originalSql: string;
  correctedSql: string;
}

/**
 * Compute a simple line-level diff between two SQL strings.
 * Returns lines tagged as 'equal', 'removed', or 'added'.
 */
function computeLineDiff(
  original: string,
  corrected: string
): Array<{ type: 'equal' | 'removed' | 'added'; text: string }> {
  const oldLines = original.split('\n');
  const newLines = corrected.split('\n');

  // LCS-based diff for accurate line matching
  const m = oldLines.length;
  const n = newLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Back-track to build diff
  const result: Array<{ type: 'equal' | 'removed' | 'added'; text: string }> = [];
  let i = m;
  let j = n;
  const stack: typeof result = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      stack.push({ type: 'equal', text: oldLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'added', text: newLines[j - 1] });
      j--;
    } else {
      stack.push({ type: 'removed', text: oldLines[i - 1] });
      i--;
    }
  }

  stack.reverse();
  result.push(...stack);
  return result;
}

const lineStyle = (type: 'equal' | 'removed' | 'added'): React.CSSProperties => {
  const base: React.CSSProperties = {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    padding: '2px 8px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  };
  if (type === 'removed') {
    return { ...base, backgroundColor: '#fdd', color: '#900', textDecoration: 'line-through' };
  }
  if (type === 'added') {
    return { ...base, backgroundColor: '#dfd', color: '#060' };
  }
  return { ...base, color: 'var(--mds-color-neutral-strong-text, #333)' };
};

const prefixChar = (type: 'equal' | 'removed' | 'added') => {
  if (type === 'removed') return '− ';
  if (type === 'added') return '+ ';
  return '  ';
};

export const SqlDiffDialog: React.FC<SqlDiffDialogProps> = ({
  open,
  onClose,
  originalSql,
  correctedSql,
}) => {
  const diffLines = React.useMemo(
    () => computeLineDiff(originalSql, correctedSql),
    [originalSql, correctedSql]
  );

  const removedCount = diffLines.filter((l) => l.type === 'removed').length;
  const addedCount = diffLines.filter((l) => l.type === 'added').length;

  return (
    <McDialog open={open} heading="AI Correction — Changes" closed={onClose} width="900px">
      <div style={{ marginBottom: 12, display: 'flex', gap: 16, fontSize: '0.85rem' }}>
        <span style={{ color: '#900', fontWeight: 600 }}>− {removedCount} removed</span>
        <span style={{ color: '#060', fontWeight: 600 }}>+ {addedCount} added</span>
      </div>

      <div
        style={{
          border: '1px solid var(--mds-color-neutral-passive-border, #ddd)',
          borderRadius: 6,
          maxHeight: 500,
          overflowY: 'auto',
          padding: '8px 0',
          backgroundColor: 'var(--mds-color-neutral-passive-background, #fafafa)',
        }}
      >
        {diffLines.map((line, idx) => (
          <div key={idx} style={lineStyle(line.type)}>
            <span style={{ opacity: 0.5, userSelect: 'none' }}>{prefixChar(line.type)}</span>
            {line.text || '\u00A0'}
          </div>
        ))}
      </div>

      <div slot="primaryAction">
        <McButton appearance="primary" onClick={onClose}>
          Close
        </McButton>
      </div>
    </McDialog>
  );
};
